import { useState } from "react";
import { RegisterContent } from "../registration/components/RegisterOverlay";
import { LoginContent } from "./login/components/LoginPopup";
import ResetPasswordPopup from "./login/components/ResetPasswordPopup";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { Overlay, OverlayContent, OverlayHeader } from "~/common/components/Overlay/Overlay";
import { Grid, Typography } from "~/common/mui";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useTranslation } from "react-i18next";
import { RightImage, RightTextContainer } from "./login/components/loginPopupStyles";

export type AuthView = "login" | "register" | "reset" | "";

interface Props {
  handleClose: () => void;
  initialView?: AuthView;
  initialEmail?: string;
  alternativeGAAction?: string;
  alternativeLoginAction?: () => void;
}

export default function AuthOverlay({
  initialView = "register",
  initialEmail = "",
  alternativeGAAction = "",
  alternativeLoginAction,
  handleClose,
}: Props) {
  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();
  const isTablet = screenSize <= useNumericScreenSize.tablet;
  const [email, setEmail] = useState(initialEmail);
  const [fullWidthContent, setFullWidthContent] = useState(false);

  const [view, setView] = useState<AuthView>(initialView);

  const onResetClick = (resetEmail: string) => {
    setEmail(resetEmail);
    setView("reset");
  };

  const onLoginClick = () => {
    setFullWidthContent(false);
    setView("login");
  };
  const onRegisterClick = () => {
    setFullWidthContent(false);
    setView("register");
  };

  const close = () => {
    setView("");
    handleClose();
  };

  if (userIsLoggedIn()) {
    return null;
  }

  return (
    <Overlay open={true}>
      <OverlayHeader onClose={close} />
      <OverlayContent sx={{ p: 0, "&:last-child": { paddingBottom: 0 } }}>
        <Grid container spacing={3}>
          <Grid
            size={isTablet || fullWidthContent ? 12 : 6}
            sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}
          >
            {view === "register" && (
              <RegisterContent onLoginClick={onLoginClick} onFullWidthChange={setFullWidthContent} />
            )}
            {view === "login" && (
              <LoginContent
                initialEmail={initialEmail}
                onResetClick={onResetClick}
                onRegisterClick={onRegisterClick}
                alternativeGAAction={alternativeGAAction}
                alternativeLoginAction={alternativeLoginAction}
                onFullWidthChange={setFullWidthContent}
              />
            )}
            {view === "reset" && <ResetPasswordPopup initialEmail={email} close={close} />}
          </Grid>
          {!isTablet && !fullWidthContent && (
            <Grid size={6}>
              <RightImage>
                <RightTextContainer>
                  <Typography variant="headlineLarge" sx={{ color: "textPrimary.main" }}>
                    {t("configure.linear.solution.heading")}
                  </Typography>

                  <Typography variant="titleLarge" sx={{ color: "textSecondary.main" }}>
                    {t("configure.linear.solution.subHeading")}
                  </Typography>
                </RightTextContainer>
              </RightImage>
            </Grid>
          )}
        </Grid>
      </OverlayContent>
    </Overlay>
  );
}
